import {apiDownload, apiRequest, apiUpload} from './http';
import * as T from './Objetos';

type Id = number;

function projectCollectionCrud<TItem>(resource: string) {
    return {
        list: async (proyectoId: Id) => {
            try {
                return await apiRequest<TItem[]>({
                    method: 'GET',
                    path: `/api/proyectos/${proyectoId}/${resource}`,
                    useTurnstile: true,
                });
            } catch (error: any) {
                if (error?.status === 404) return [];
                throw error;
            }
        },

        get: (proyectoId: Id, id: Id) =>
            apiRequest<TItem>({
                method: 'GET',
                path: `/api/proyectos/${proyectoId}/${resource}/${id}`,
                useTurnstile: true,
            }),


        create: (proyectoId: Id, data: TItem) =>
            apiRequest<TItem>({
                method: 'POST',
                path: `/api/proyectos/${proyectoId}/${resource}`,
                body: data,
            }),

        update: (proyectoId: Id, id: Id, data: TItem) =>
            apiRequest<TItem>({
                method: 'PUT',
                path: `/api/proyectos/${proyectoId}/${resource}/${id}`,
                body: data,
            }),

        delete: (proyectoId: Id, id: Id) =>
            apiRequest<void>({
                method: 'DELETE',
                path: `/api/proyectos/${proyectoId}/${resource}/${id}`,
            }),
    };
}

function projectSingletonCrud<TItem>(resource: string) {
    return {
        list: async (proyectoId: Id) => {
            try {
                return await apiRequest<TItem[]>({
                    method: 'GET',
                    path: `/api/proyectos/${proyectoId}/${resource}`,
                    useTurnstile: true, // Añadido para enviar el token
                });
            } catch (error: any) {
                if (error?.status === 404) return [];
                throw error;
            }
        },

        get: async (proyectoId: Id) => {
            try {
                const response = await apiRequest<TItem[] | TItem>({
                    method: 'GET',
                    path: `/api/proyectos/${proyectoId}/${resource}`,
                    useTurnstile: true, // Añadido para enviar el token
                });
                return Array.isArray(response) ? (response[0] ?? null) : response;
            } catch (error: any) {
                if (error?.status === 404) return null;
                throw error;
            }
        },

        save: async (proyectoId: Id, data: TItem & { id?: number }) => {
            const isUpdate = Boolean(data?.id);

            return apiRequest<TItem>({
                method: isUpdate ? 'PUT' : 'POST',
                // URL LIMPIA: /api/proyectos/15/seguimiento-plataforma (sin /id al final)
                path: `/api/proyectos/${proyectoId}/${resource}`,
                body: data,
            });
        },

        delete: async (proyectoId: Id, id: Id) =>
            apiRequest<void>({
                method: 'DELETE',
                path: `/api/proyectos/${proyectoId}/${resource}/${id}`,
            }),
    };
}

export const proyectosApi = {
    list: (params: { q?: string; estado?: string } = {}) => {
        const query = new URLSearchParams();
        if (params.q) query.set('q', params.q);
        if (params.estado) query.set('estado', params.estado);
        const queryString = query.toString();

        return apiRequest<T.ProyectoDTO[]>({
            method: 'GET',
            path: `/api/proyectos${queryString ? `?${queryString}` : ''}`,
            useTurnstile: true
        });
    },
    getResumen: () =>
        apiRequest<T.ProyectoResumenDTO>({
            method: 'GET',
            path: '/api/proyectos/resumen',
            useTurnstile: true
        }),
    checkVersion: (id: Id, version: number) =>
        apiRequest<void>({
            method: 'GET',
            path: `/api/proyectos/${id}/version?version=${version}`,
            useTurnstile: true
        }),
    get: (id: Id) =>
        apiRequest<T.ProyectoDTO>({
            method: 'GET',
            path: `/api/proyectos/${id}`,
            useTurnstile: true
        }),

    create: (data: Partial<T.ProyectoDTO> = {}) =>
        apiRequest<T.ProyectoDTO>({
            method: 'POST',
            path: '/api/proyectos',
            body: data,
        }),

    update: (id: Id, data: T.ProyectoDTO) =>
        apiRequest<T.ProyectoDTO>({
            method: 'PUT',
            path: `/api/proyectos/${id}`,
            body: data,
        }),

    delete: (id: Id) =>
        apiRequest<void>({
            method: 'DELETE',
            path: `/api/proyectos/${id}`,
        }),
};

export const proyectosPublicosApi = {
    list: (params: { q?: string; estado?: string } = {}) => {
        const query = new URLSearchParams();
        if (params.q) query.set('q', params.q);
        if (params.estado) query.set('estado', params.estado);
        const queryString = query.toString();

        return apiRequest<T.ProyectoDTO[]>({
            method: 'GET',
            path: `/api/publicos/proyectos${queryString ? `?${queryString}` : ''}`,
            useTurnstile: true,
        });
    },
    get: (id: Id) => apiRequest<T.ProyectoDTO>({
        method: 'GET',
        path: `/api/publicos/proyectos/${id}`,
        useTurnstile: true,
    }),
};

export const informacionGeneralApi = {
    get: async (proyectoId: Id): Promise<T.ProyectoInformacionGeneralDTO | null> => {
        try {
            const response = await apiRequest<T.ProyectoInformacionGeneralDTO[]>({
                method: 'GET',
                path: `/api/proyectos/${proyectoId}/Informacion_General`,
                useTurnstile: true
            });
            return response.length > 0 ? response[0] : null;
        } catch (error: any) {
            if (error?.status === 404) return null;
            throw error;
        }
    },

    save: async (proyectoId: Id, data: T.ProyectoInformacionGeneralDTO) => {
        const path = data.id
            ? `/api/proyectos/${proyectoId}/Informacion_General/${data.id}`
            : `/api/proyectos/${proyectoId}/Informacion_General`;

        return apiRequest<T.ProyectoInformacionGeneralDTO>({
            method: data.id ? 'PUT' : 'POST',
            path: path,
            body: data,
        });
    }
};

export const datosContablesApi = {
    get: async (proyectoId: Id): Promise<T.ProyectoDatosContablesDTO | null> => {
        try {
            return await apiRequest<T.ProyectoDatosContablesDTO>({
                method: 'GET',
                path: `/api/proyectos/${proyectoId}/Datos_Contables`,
                useTurnstile: true
            });
        } catch (error: any) {
            if (error?.status === 404) return null;
            throw error;
        }
    },

    save: async (proyectoId: Id, data: T.ProyectoDatosContablesDTO) => {
        const path = data.id
            ? `/api/proyectos/${proyectoId}/Datos_Contables/${data.id}`
            : `/api/proyectos/${proyectoId}/Datos_Contables`;

        return apiRequest<T.ProyectoDatosContablesDTO>({
            method: data.id ? 'PUT' : 'POST',
            path,
            body: data,
        });
    },
};



export const datosContratosApi = {
    get: async (proyectoId: Id): Promise<T.ProyectoDatosContratosDTO | null> => {
        try {
            return await apiRequest<T.ProyectoDatosContratosDTO>({
                method: 'GET',
                path: `/api/proyectos/${proyectoId}/Datos_Contratos_P`,
                useTurnstile: true
            });
        } catch (error: any) {
            if (error?.status === 404) return null;
            throw error;
        }
    },
    save: async (proyectoId: Id, data: T.ProyectoDatosContratosDTO) => {
        // Si tiene ID, PUT a la URL del recurso, si no, POST al contenedor
        const path = data.id
            ? `/api/proyectos/${proyectoId}/Datos_Contratos_P/${data.id}`
            : `/api/proyectos/${proyectoId}/Datos_Contratos_P`;

        return apiRequest<T.ProyectoDatosContratosDTO>({
            method: data.id ? 'PUT' : 'POST',
            path,
            body: data,
        });
    },
};

export const requerimientosApi = {
    ...projectCollectionCrud<T.ProyectoRequerimientosDTO>('Requerimientos'),
    remove: (proyectoId: Id, id: Id) =>
        apiRequest<void>({
            method: 'DELETE',
            path: `/api/proyectos/${proyectoId}/Requerimientos/${id}`,
        }),
};
export const accionesSeguimientoApi = projectCollectionCrud<T.ProyectoAccionSeguimientoDTO>('Acciones_Seguimiento');
export const autorizacionesApi = projectCollectionCrud<T.ProyectoAutorizacionesSolicitadasDTO>('Autorizaciones_Solicitadas');
export const reunionesApi = projectCollectionCrud<T.ProyectoReunionesDTO>('Reuniones');
export const hitosApi = projectCollectionCrud<T.ProyectoHitosYObjetivosDTO>('Hitos_Y_Objetivos');
export const informesApi = projectCollectionCrud<T.ProyectoInformeGestionDTO>('Informes_De_Gestion');
export const participacionApi = projectCollectionCrud<T.ProyectoParticipacionCiudadanaDTO>('Participacion_Ciudadana');
export const plazosApi = projectCollectionCrud<T.ProyectoPlazosYJustificacionDTO>('Plazos_Y_Justificacion');
export const comunicacionApi = projectCollectionCrud<T.ProyectoComunicacionDTO>('Comunicacion');
export const comunicacionesPublicasApi = {
    listar: (proyectoId: Id) =>
        apiRequest<T.ProyectoComunicacionDTO[]>({
            method: 'GET',
            path: `/api/publicos/proyectos/${proyectoId}/comunicacion`,
            useTurnstile: true,
        }),
};
export const seguimientoPlataformaApi = projectSingletonCrud<T.ProyectoSeguimientoPlataformaDTO>('Seguimiento_Plataforma');
export const seguimientoCoffeApi = projectSingletonCrud<T.ProyectoSeguimientoCoFFEDTO>('Seguimiento_Coffe');

export const certificacionesObraApi = {
    get: async (proyectoId: Id) => {
        try {
            return await apiRequest<T.ProyectoCertificacionesObraDTO | null>({
                method: 'GET',
                path: `/api/proyectos/${proyectoId}/Certificaciones_De_Obra`,
                useTurnstile: true
            });
        } catch (error: any) {
            if (error?.status === 404) return null;
            throw error;
        }
    },

    save: (proyectoId: Id, data: T.ProyectoCertificacionesObraDTO) => {
        const path = `/api/proyectos/${proyectoId}/Certificaciones_De_Obra`;

        return apiRequest<T.ProyectoCertificacionesObraDTO>({
            method: data?.id ? 'PUT' : 'POST',
            path: path,
            body: data,
        });
    },

    delete: (proyectoId: Id) =>
        apiRequest<void>({
            method: 'DELETE',
            path: `/api/proyectos/${proyectoId}/Certificaciones_De_Obra`,
        }),
};

export const documentosAdjuntosApi = {
    listar: (proyectoId: Id, ambito: T.DocumentoAdjuntoAmbito, subcontratistaId?: number) => {
        const query = new URLSearchParams({ambito});
        if (subcontratistaId) query.set('subcontratistaId', String(subcontratistaId));
        return apiRequest<T.DocumentoAdjuntoDTO[]>({
            method: 'GET',
            path: `/api/proyectos/${proyectoId}/documentos?${query.toString()}`,
        });
    },
    subir: (proyectoId: Id, data: {
        ambito: T.DocumentoAdjuntoAmbito;
        subcontratistaId?: number;
        nombre: string;
        descripcion: string;
        fecha: string;
        archivo: File;
    }) => {
        const form = new FormData();
        form.append('ambito', data.ambito);
        if (data.subcontratistaId) form.append('subcontratistaId', String(data.subcontratistaId));
        form.append('nombre', data.nombre);
        form.append('descripcion', data.descripcion);
        form.append('fecha', data.fecha);
        form.append('archivo', data.archivo);
        return apiUpload<T.DocumentoAdjuntoDTO>(`/api/proyectos/${proyectoId}/documentos`, form);
    },
    contenido: (proyectoId: Id, documentoId: Id, descargar = false) =>
        apiDownload(`/api/proyectos/${proyectoId}/documentos/${documentoId}/contenido?descargar=${descargar}`),
    eliminar: (proyectoId: Id, documentoId: Id) => apiRequest<void>({
        method: 'DELETE',
        path: `/api/proyectos/${proyectoId}/documentos/${documentoId}`,
    }),
};

export const documentosPublicosApi = {
    listar: (proyectoId: Id, ambito: 'LOGO_PROYECTO' | 'GALERIA_PROYECTO' | 'ACTA_REUNION', reunionId?: number) => {
        const query = new URLSearchParams({ambito});
        if (reunionId) query.set('reunionId', String(reunionId));
        return apiRequest<T.DocumentoAdjuntoDTO[]>({
            method: 'GET',
            path: `/api/publicos/proyectos/${proyectoId}/documentos?${query.toString()}`,
        });
    },
    contenidoUrl: (proyectoId: Id, documentoId: Id) =>
        `/api/publicos/proyectos/${proyectoId}/documentos/${documentoId}/contenido`,
    contenido: (proyectoId: Id, documentoId: Id) =>
        apiDownload(`/api/publicos/proyectos/${proyectoId}/documentos/${documentoId}/contenido`),
};

export const reunionesPublicasApi = {
    listar: (proyectoId: Id) => apiRequest<T.ProyectoReunionesDTO[]>({
        method: 'GET', path: `/api/publicos/proyectos/${proyectoId}/reuniones`, useTurnstile: true,
    }),
};


export const contratosPublicosApi = {
    listar: (proyectoId: Id) =>
        apiRequest<T.PublicoDatosContratosDTO>({
            method: 'GET',
            path: `/api/publicos/proyectos/${proyectoId}/Datos_Contratos_Pu`,
            useTurnstile: true
        }),
};

export const proyectoBloqueoApi = {
    bloquear: (idProyecto: number, usuarioId: number) =>
        apiRequest<T.BloqueoResponse>({
            method: 'POST',
            path: `/api/proyectos/${idProyecto}/Bloqueo/${usuarioId}`,
        }),
    heartbeat: (idProyecto: number, usuarioId: number) =>
        apiRequest<T.BloqueoResponse>({
            method: 'PUT',
            path: `/api/proyectos/${idProyecto}/Bloqueo/${usuarioId}`,
        }),
    liberar: (idProyecto: number, usuarioId: number) =>
        apiRequest<T.BloqueoResponse>({
            method: 'DELETE',
            path: `/api/proyectos/${idProyecto}/Bloqueo/${usuarioId}`,
        }),
    estaBloqueado: (idProyecto: number) =>
        apiRequest<boolean>({
            method: 'GET',
            path: `/api/proyectos/${idProyecto}/Bloqueo`,
        }),
    obtenerDetalle: (idProyecto: number) =>
        apiRequest<T.ProyectoBloqueoEdicionDTO>({
            method: 'GET',
            path: `/api/proyectos/${idProyecto}/Bloqueo/detalle`,
        }),
};
export const authApi = {
    login: (data: T.SolicitudLogin) =>
        apiRequest<T.RespuestaAutenticacion>({
            method: 'POST',
            path: '/api/autenticacion/iniciar_sesion',
            body: data,
            useTurnstile: true
        }),

    logout: () =>
        apiRequest<void>({
            method: 'POST',
            path: '/api/autenticacion/cerrar_sesion'
        }),

    crearUsuario: (data: T.SolicitudCrearUsuario) =>
        apiRequest<T.RespuestaUsuario>({
            method: 'POST',
            path: '/api/autenticacion/usuarios',
            body: data
        }),
        
    heartbeat: () =>
        apiRequest<void>({
            method: 'POST',
            path: '/api/autenticacion/heartbeat'
        })
};

export const usuariosApi = {
    listar: () => apiRequest<T.UsuarioDTO[]>({
        method: 'GET',
        path: '/api/Usuario',
    }),
    restablecerPassword: (id: number, password: string) => apiRequest<void>({
        method: 'PUT',
        path: `/api/Usuario/${id}/password`,
        body: {password},
    }),
    eliminar: (id: number) => apiRequest<void>({
        method: 'DELETE',
        path: `/api/Usuario/${id}`,
    }),
};
